import { supabase } from '@/lib/supabase';
import type { DateRange } from '@/lib/salesAnalytics';

/**
 * One build's sell-through against what could still be built.
 *
 * `sold` is units posted in the window for this model, matched on the SKU, so
 * it agrees with the sales tab. `onHand` is serial-derived and all-time,
 * because "which machines are still unsold" is a question about individual
 * units. The two therefore do not form an equation with `built` - they are
 * three separate measurements.
 */
export interface SellThroughRow {
  buildId: string;
  buildSku: string;
  description: string | null;
  /** Units sold in the window, net of returns, matched by SKU. */
  sold: number;
  /** Units posted by PAO records in the window. */
  built: number;
  /** Built and never net-sold, as of now. All time, not the window. */
  onHand: number;
  /** How many more the current component availability allows. */
  canBuild: number;
  /** The component that sets that ceiling. */
  limitingSku: string | null;
  limitingAvailable: number | null;
  /** True when no active config was ever ticked and first-per-category was assumed. */
  assumedConfig: boolean;
}

/** How much of the window's volume carries an item_no that matches a build. */
export interface SellThroughCoverage {
  /** Net units sold in the window, revival packs excluded - the sales tab's figure. */
  totalUnits: number;
  attributedUnits: number;
  unmatchedUnits: number;
  /** The item numbers with no build record, so a namespace drift is visible. */
  unmatchedSkus: string[];
}

/** PostgREST sends numeric as a string and bigint as a number; normalise both. */
function num(v: unknown): number {
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : 0;
}

export async function fetchSellThrough(range: DateRange): Promise<SellThroughRow[]> {
  const { data, error } = await supabase.rpc('model_sell_through', {
    p_from: range.from,
    p_to: range.to,
  });
  if (error) throw error;

  return ((data ?? []) as Record<string, unknown>[]).map(r => ({
    buildId: String(r.build_id),
    buildSku: String(r.build_sku ?? ''),
    description: (r.description as string | null) ?? null,
    sold: num(r.sold_units),
    built: num(r.built_units),
    onHand: num(r.on_hand),
    canBuild: num(r.can_build),
    limitingSku: (r.limiting_sku as string | null) ?? null,
    limitingAvailable: r.limiting_available == null ? null : num(r.limiting_available),
    assumedConfig: !!r.is_assumed_config,
  }));
}

export async function fetchSellThroughCoverage(range: DateRange): Promise<SellThroughCoverage> {
  const { data, error } = await supabase.rpc('model_sell_through_coverage', {
    p_from: range.from,
    p_to: range.to,
  });
  if (error) throw error;

  // The RPC returns a single row; PostgREST still wraps it in an array.
  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | undefined;
  return {
    totalUnits: num(row?.total_units),
    attributedUnits: num(row?.attributed_units),
    unmatchedUnits: num(row?.unmatched_units),
    unmatchedSkus: (row?.unmatched_skus as string[] | null) ?? [],
  };
}

/** Newest stock snapshot on record, or null before the first capture runs. */
export async function fetchLatestSnapshotDate(): Promise<string | null> {
  const { data, error } = await supabase
    .from('component_stock_snapshots')
    .select('taken_on')
    .order('taken_on', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data?.taken_on as string | undefined) ?? null;
}
